import { useState } from 'react';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { RigTyreDiagram, type RigTyreSelection } from '../components/RigTyreDiagram';
import { RigTyreSection } from '../components/RigTyreSection';
import { VehicleTyreMoveModal } from '../components/VehicleTyreMoveModal';
import { useCanManageTyres } from '../hooks/use-can-manage-tyres';
import { useRigVehicleTyres } from '../hooks/use-rig-vehicle-tyres';
import { VehicleTyresTab } from './VehicleTyresTab';

interface RigTyresTabProps {
  truckId: string;
  trailerId: string;
}

/**
 * The Tyres tab of a unit (gitaumoses4/chukua#390): the truck's positions and its trailer's, beside the two drawn as the one rig
 * they are. A tyre still belongs to its own vehicle, so every fit, replace and rotate is that vehicle's, and a tyre on the
 * trailer goes with it to another truck. When either vehicle can't be drawn (no layout yet), each is shown on its own.
 */
export function RigTyresTab({ truckId, trailerId }: RigTyresTabProps) {
  const truck = useRigVehicleTyres('truck', truckId);
  const trailer = useRigVehicleTyres('trailer', trailerId);
  const canManage = useCanManageTyres();
  const [selected, setSelected] = useState<RigTyreSelection | null>(null);

  if (truck.state.status === 'loading' || trailer.state.status === 'loading') return <TableSkeleton label="Loading tyres" />;
  if (truck.state.status !== 'loaded' || trailer.state.status !== 'loaded') {
    return (
      <div className="flex flex-col gap-8">
        <VehicleTyresTab vehicleKind="truck" vehicleId={truckId} />
        <VehicleTyresTab vehicleKind="trailer" vehicleId={trailerId} />
      </div>
    );
  }

  const selectedOf = (vehicle: RigTyreSelection['vehicle']) => (selected?.vehicle === vehicle ? selected.code : null);
  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-muted">Click a position, or a tyre on the rig, to find it.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-6">
          {[
            { vehicle: 'truck' as const, part: truck, layout: truck.state.layout },
            { vehicle: 'trailer' as const, part: trailer, layout: trailer.state.layout },
          ].map(({ vehicle, part, layout }) => (
            <RigTyreSection
              key={vehicle}
              vehicleKind={vehicle}
              layout={layout}
              actions={part.actions}
              canManage={canManage}
              selectedCode={selectedOf(vehicle)}
              onSelect={(code) => setSelected({ vehicle, code })}
            />
          ))}
        </div>
        <RigTyreDiagram truck={truck.state.layout} trailer={trailer.state.layout} selected={selected} onSelect={setSelected} />
      </div>
      <VehicleTyreMoveModal actions={truck.actions} />
      <VehicleTyreMoveModal actions={trailer.actions} />
    </div>
  );
}
